import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Pencil, MapPin, Briefcase, Clock, Euro, Calendar,
  Building, CheckCircle, Save, Send, ChevronRight,
  ThumbsUp, Gift, Info, AlertCircle, Mail, Star, FileText, Loader2
} from 'lucide-react';
import jobService, { CreateJobData, Job, JobResponse } from '../../services/jobService';
import { useAuth } from '../../contexts/AuthContext';
import './JobFormPreview.scss';

interface JobFormPreviewProps {
  job?: Job;
  onSuccess?: (job: Job) => void;
  onCancel?: () => void;
}

interface CVTemplates {
  [category: string]: Array<{ id: number; category: string; value: string }>;
}

const JobFormPreview: React.FC<JobFormPreviewProps> = ({ job, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState<CreateJobData>({
    title: '',
    company_name: '',
    website_url: '',
    job_language: 'Lietuvių',
    work_field: '',
    location: '',
    position_type: '',
    job_type: '',
    work_time: '',
    positions: '',
    description: '',
    requirements: '',
    salary_min: undefined,
    salary_max: undefined,
    salary_currency: 'EUR',
    hide_salary: false,
    benefits: '',
    company_offers: '',
    guarantee_confidentiality: false,
    inform_selected_only: false,
    additional_info: '',
    start_date_type: '',
    application_deadline: '',
    company_activity: '',
    contact_email: '',
    company_description: '',
    client_id: null,
    hide_client: false,
    status: 'draft'
  });

  const [cvTemplates, setCvTemplates] = useState<CVTemplates>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [titleSearch, setTitleSearch] = useState('');
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);
  const titleFieldRef = React.useRef<HTMLDivElement>(null);
  const [employerClients, setEmployerClients] = useState<Array<{ id: number; name: string }>>([]);
  const isRecruitmentAgency = !!user?.is_recruitment_agency;

  // Load CV templates and employer clients
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_URL}/cv-templates?grouped=true`);
        if (response.ok) {
          const result = await response.json();
          setCvTemplates(result.data || {});
        }
      } catch (error) {
        console.error('Error loading CV templates:', error);
      }
    };
    loadTemplates();
  }, []);

  useEffect(() => {
    if (!isRecruitmentAgency) return;
    const loadClients = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${API_URL}/employer-clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          if (result.success) setEmployerClients(result.data);
        }
      } catch {}
    };
    loadClients();
  }, [isRecruitmentAgency]);

  // Prefill company info from user profile (only for new jobs)
  useEffect(() => {
    if (user && !job) {
      const userAny = user as any;
      const profile = userAny.profile || {};
      
      // Profile may store company description as 'bio'
      const profileCompanyDescription = profile.company_description || profile.bio || '';
      
      setFormData(prev => ({
        ...prev,
        company_name: userAny.company_name || profile.company_name || prev.company_name,
        website_url: userAny.website_url || profile.website_url || prev.website_url,
        contact_email: userAny.email || prev.contact_email,
        company_description: userAny.company_description || profileCompanyDescription || prev.company_description,
        company_activity: userAny.company_activity || profile.company_activity || prev.company_activity,
      }));
    }
  }, [user, job]);

  // Populate form if editing existing job
  useEffect(() => {
    if (job) {
      const jobAny = job as any;
      setFormData({
        title: job.title || '',
        company_name: jobAny.company_name || '',
        website_url: jobAny.website_url || '',
        job_language: jobAny.job_language || 'Lietuvių',
        work_field: jobAny.work_field || '',
        location: job.location || '',
        position_type: jobAny.position_type || '',
        job_type: job.job_type || '',
        work_time: jobAny.work_time || '',
        positions: jobAny.positions || '',
        description: job.description || '',
        requirements: job.requirements || '',
        salary_min: job.salary_min,
        salary_max: job.salary_max,
        salary_currency: job.salary_currency || 'EUR',
        hide_salary: jobAny.hide_salary || false,
        benefits: job.benefits || '',
        company_offers: jobAny.company_offers || '',
        guarantee_confidentiality: jobAny.guarantee_confidentiality || false,
        inform_selected_only: jobAny.inform_selected_only || false,
        additional_info: jobAny.additional_info || '',
        start_date_type: jobAny.start_date_type || '',
        application_deadline: jobAny.application_deadline ? jobAny.application_deadline.split('T')[0] : '',
        company_activity: jobAny.company_activity || '',
        contact_email: jobAny.contact_email || '',
        company_description: jobAny.company_description || '',
        client_id: jobAny.client_id || null,
        hide_client: jobAny.hide_client || false,
        status: job.status === 'closed' || job.status === 'archived' ? 'draft' : job.status
      });
    }
  }, [job]);

  // Close title dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (titleFieldRef.current && !titleFieldRef.current.contains(e.target as Node)) {
        setShowTitleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredPositions = (cvTemplates.position || []).filter(p =>
    !titleSearch || p.value.toLowerCase().includes(titleSearch.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name.includes('salary') && value ? parseFloat(value) || undefined : value)
    }));
  };

  const handleSubmit = async (action: 'save' | 'publish') => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (action === 'publish') {
        const missingFields: string[] = [];
        if (!formData.title?.trim()) missingFields.push('Pareigybės pavadinimas');
        if (!formData.location?.trim()) missingFields.push('Darbovietės miestas');
        if (!formData.description?.trim()) missingFields.push('Darbo aprašymas');
        
        if (missingFields.length > 0) {
          setError(`Užpildykite privalomus laukus: ${missingFields.join(', ')}`);
          setLoading(false);
          return;
        }
      } else {
        if (!formData.title?.trim()) {
          setError('Įveskite bent pareigybės pavadinimą');
          setLoading(false);
          return;
        }
      }

      const submitData: CreateJobData = {
        ...formData,
        status: action === 'publish' ? 'published' : 'draft'
      };

      let result: JobResponse;
      if (job?.id) {
        result = await jobService.updateJob(job.id, submitData);
      } else {
        result = await jobService.createJob(submitData);
      }

      if (result.success && result.data) {
        const successMessage = action === 'publish' 
          ? 'Darbo skelbimas sėkmingai publikuotas!' 
          : 'Juodraštis išsaugotas!';
        
        setSuccess(successMessage);
        
        // Smooth transition - show success briefly then redirect
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(result.data!.job);
          }, 800);
        }
      } else {
        setError(result.message || 'Nepavyko išsaugoti darbo skelbimo');
      }
    } catch (error) {
      setError('Įvyko klaida. Bandykite dar kartą.');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (field: string) => setEditingField(field);
  const stopEditing = () => setEditingField(null);

  const userAny = user as any;
  const companyLogo = userAny?.profile?.profile_image || userAny?.profile_image;
  const logoUrl = companyLogo 
    ? `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}/uploads/profiles/${companyLogo}`
    : null;

  // Render editable text section
  const renderTextSection = (field: string, icon: React.ReactNode, label: string, placeholder: string, required?: boolean) => {
    const value = (formData as any)[field] || '';
    const isEditing = editingField === field;

    return (
      <div className="content-row">
        <div className="nav-item" onClick={() => !isEditing && startEditing(field)}>
          <div className="nav-icon">{icon}</div>
          <span>{label} {required && <span className="req">*</span>}</span>
        </div>
        <div className="content-box" onClick={() => !isEditing && startEditing(field)}>
          {isEditing ? (
            <div className="edit-mode" onClick={(e) => e.stopPropagation()}>
              <textarea
                name={field}
                value={value}
                onChange={handleInputChange}
                placeholder={placeholder}
                rows={5}
                autoFocus
              />
              <button className="btn-done" onClick={stopEditing}>
                <CheckCircle size={14} /> Patvirtinti
              </button>
            </div>
          ) : (
            <div className={`content-text ${!value ? 'placeholder' : ''}`}>
              {value ? <div dangerouslySetInnerHTML={{ __html: value.replace(/\n/g, '<br>') }} /> : (
                <span className="click-hint"><Pencil size={12} /> {placeholder}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="job-form-preview-wysiwyg">
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <span onClick={() => navigate('/')} className="breadcrumb-link">Pradžia</span>
        <ChevronRight size={14} className="separator" />
        <span onClick={() => navigate('/jobs?mode=employer')} className="breadcrumb-link">Skelbimai</span>
        <ChevronRight size={14} className="separator" />
        <span className="current">{job ? 'Redaguoti skelbimą' : 'Naujas skelbimas'}</span>
      </div>

      {error && <div className="alert alert-error"><AlertCircle size={18} />{error}</div>}
      
      {/* Success Toast - Fixed at top */}
      {success && (
        <div className="toast-success">
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* ========== SECTION 1: COMPANY INFO (Always Expanded) ========== */}
      <div className="form-section">
        <h2 className="section-title"><Building size={18} /> Informacija apie įmonę</h2>
        
        <div className="company-form">
          <div className="company-logo-area">
            {logoUrl ? <img src={logoUrl} alt="Logo" /> : <div className="logo-placeholder"><Building size={32} /></div>}
            <span className="logo-hint">Logotipas iš profilio</span>
          </div>

          <div className="company-fields">
            <div className="field-row">
              <div className="field-group">
                <label>Skelbimo kalba</label>
                <select name="job_language" value={formData.job_language} onChange={handleInputChange}>
                  <option value="Lietuvių">Lietuvių</option>
                  {(cvTemplates.job_language || []).map((t, i) => (
                    <option key={i} value={t.value}>{t.value}</option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <label>Įmonės pavadinimas</label>
                <input type="text" name="company_name" value={formData.company_name || ''} onChange={handleInputChange} placeholder="UAB Įmonė" />
              </div>
            </div>

            <div className="field-row">
              <div className="field-group">
                <label>Įmonės veiklos sritis</label>
                <select name="company_activity" value={formData.company_activity} onChange={handleInputChange}>
                  <option value="">Pasirinkite</option>
                  {(cvTemplates.company_activity || []).map((t, i) => (
                    <option key={i} value={t.value}>{t.value}</option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <label>Įmonės internetinė svetainė</label>
                <input type="text" name="website_url" value={formData.website_url || ''} onChange={handleInputChange} placeholder="https://www.imone.lt" />
              </div>
            </div>

            <div className="field-row">
              <div className="field-group">
                <label><Mail size={14} /> El. paštas (gausite informacinius laiškus)</label>
                <input type="email" name="contact_email" value={formData.contact_email} onChange={handleInputChange} placeholder="info@imone.lt" />
              </div>
            </div>

            <div className="field-group full-width">
              <label>Trumpas įmonės veiklos aprašymas</label>
              <textarea name="company_description" value={formData.company_description} onChange={handleInputChange} placeholder="Aprašykite savo įmonės veiklą..." rows={3} />
            </div>
          </div>
        </div>
      </div>

      {/* ========== CLIENT SECTION (recruitment agencies only) ========== */}
      {isRecruitmentAgency && employerClients.length > 0 && (
        <div className="form-section">
          <h2 className="section-title"><Building size={18} /> Klientas</h2>
          <div className="form-card">
            <div className="form-row">
              <div className="form-group">
                <label>Pasirinkite klientą</label>
                <select
                  value={formData.client_id || ''}
                  onChange={e => setFormData(prev => ({ ...prev, client_id: e.target.value ? parseInt(e.target.value) : null }))}
                >
                  <option value="">-- Nepasirinktas --</option>
                  {employerClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {formData.client_id && (
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!formData.hide_client}
                    onChange={e => setFormData(prev => ({ ...prev, hide_client: e.target.checked }))}
                  />
                  Nerodyti kliento pavadinimo skelbime
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== SECTION 2: JOB INFO ========== */}
      <div className="form-section">
        <h2 className="section-title"><Briefcase size={18} /> Informacija apie darbą</h2>

        {/* Job Title */}
        <div className="title-field" ref={titleFieldRef} onClick={() => { startEditing('title'); setTitleSearch(formData.title || ''); setShowTitleDropdown(true); }}>
          {editingField === 'title' ? (
            <div className="edit-mode" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={titleSearch}
                onChange={(e) => { setTitleSearch(e.target.value); setShowTitleDropdown(true); setFormData(prev => ({ ...prev, title: e.target.value })); }}
                placeholder="Įveskite pareigybės pavadinimą"
                autoFocus
                onFocus={() => setShowTitleDropdown(true)}
              />
              <button className="btn-done" onClick={() => { stopEditing(); setShowTitleDropdown(false); }}><CheckCircle size={14} /> Patvirtinti</button>
              {showTitleDropdown && filteredPositions.length > 0 && (
                <ul className="title-dropdown">
                  {filteredPositions.map(p => (
                    <li key={p.id} onClick={() => { setFormData(prev => ({ ...prev, title: p.value })); setTitleSearch(p.value); setShowTitleDropdown(false); stopEditing(); }}>
                      {p.value}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <h1 className={!formData.title ? 'placeholder' : ''}>
              {formData.title || 'Pareigybės pavadinimas *'}
              <Pencil size={16} className="edit-icon" />
            </h1>
          )}
        </div>

        {/* Content Sections */}
        <div className="content-sections">
          {renderTextSection('description', <FileText size={18} />, 'Darbo aprašymas', 'Spustelėkite ir aprašykite darbo pareigas...', true)}
          {renderTextSection('requirements', <ThumbsUp size={18} />, 'Reikalavimai kandidatams', 'Spustelėkite ir įveskite reikalavimus...')}
          {renderTextSection('benefits', <Star size={18} />, 'Privalumai', 'Spustelėkite ir įveskite privalumus...')}
          {renderTextSection('company_offers', <Gift size={18} />, 'Įmonė siūlo', 'Spustelėkite ir įveskite ką siūlote...')}
          {renderTextSection('additional_info', <Info size={18} />, 'Papildoma informacija', 'Spustelėkite ir įveskite informaciją...')}
        </div>

        {/* Meta Fields (Always Expanded) */}
        <div className="meta-section-expanded">
          <h3>Darbo sąlygos</h3>
          
          <div className="meta-grid">
            <div className="meta-field">
              <label><MapPin size={14} /> Darbovietės miestas <span className="req">*</span></label>
              <select name="location" value={formData.location} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.city || cvTemplates.work_location || []).map((t, i) => (
                  <option key={i} value={t.value}>{t.value}</option>
                ))}
              </select>
            </div>
            <div className="meta-field">
              <label><Briefcase size={14} /> Darbo sritis</label>
              <select name="work_field" value={formData.work_field} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.work_field || []).map((t, i) => (
                  <option key={i} value={t.value}>{t.value}</option>
                ))}
              </select>
            </div>
            <div className="meta-field">
              <label>Pareigybės tipas</label>
              <select name="position_type" value={formData.position_type} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.position_type || []).map((t, i) => (
                  <option key={i} value={t.value}>{t.value}</option>
                ))}
              </select>
            </div>
            <div className="meta-field">
              <label><Clock size={14} /> Darbo tipas</label>
              <select name="job_type" value={formData.job_type} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.work_type || []).map((t, i) => (
                  <option key={i} value={t.value}>{t.value}</option>
                ))}
              </select>
            </div>
            <div className="meta-field">
              <label><Clock size={14} /> Darbo laikas</label>
              <select name="work_time" value={formData.work_time} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.work_time || []).map((t, i) => (
                  <option key={i} value={t.value}>{t.value}</option>
                ))}
              </select>
            </div>
            <div className="meta-field">
              <label>Darbo pradžia</label>
              <select name="start_date_type" value={formData.start_date_type} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.start_date_type || []).map((t, i) => (
                  <option key={i} value={t.value}>{t.value}</option>
                ))}
              </select>
            </div>
            <div className="meta-field">
              <label><Euro size={14} /> Siūlomas atlyginimas (netto) EUR</label>
              <div className="salary-inputs">
                <span>nuo</span>
                <input type="number" name="salary_min" value={formData.salary_min || ''} onChange={handleInputChange} placeholder="100" min="0" />
                <span>iki</span>
                <input type="number" name="salary_max" value={formData.salary_max || ''} onChange={handleInputChange} placeholder="5000" min="0" />
              </div>
            </div>
            <div className="meta-field">
              <label><Calendar size={14} /> Prašymus siųsti iki</label>
              <input type="date" name="application_deadline" value={formData.application_deadline} onChange={handleInputChange} />
            </div>
          </div>
        </div>

        {/* Checkboxes */}
        <div className="checkbox-section">
          <h3>Pažymėkite, jeigu norite, kad tai būtų rodoma skelbime:</h3>
          <div className="checkbox-row">
            <label><input type="checkbox" name="guarantee_confidentiality" checked={formData.guarantee_confidentiality} onChange={handleInputChange} /> Konfidencialumą garantuojame</label>
            <label><input type="checkbox" name="inform_selected_only" checked={formData.inform_selected_only} onChange={handleInputChange} /> Informuosime tik atrinktus kandidatus</label>
            <label><input type="checkbox" name="hide_salary" checked={formData.hide_salary} onChange={handleInputChange} /> Nerodyti atlyginimo skelbime</label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="form-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={loading}>Atšaukti</button>
        <div className="action-buttons-right">
          <button type="button" className={`btn btn-save ${loading ? 'loading' : ''}`} onClick={() => handleSubmit('save')} disabled={loading}>
            {loading ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
            {loading ? 'Saugoma...' : 'Išsaugoti juodraštį'}
          </button>
          <button type="button" className={`btn btn-publish ${loading ? 'loading' : ''}`} onClick={() => handleSubmit('publish')} disabled={loading}>
            {loading ? <Loader2 size={16} className="spinner" /> : <Send size={16} />}
            {loading ? 'Publikuojama...' : 'Publikuoti'}
          </button>
        </div>
      </div>

      <p className="required-note">* privalomi laukai</p>
    </div>
  );
};

export default JobFormPreview;
